import React, { useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Task } from '../api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { formattedDate } from 'shared/utils/date';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopes } from '@fortawesome/pro-light-svg-icons';
import { InvestmentSplitWithAnswers } from './utils';
import AllocationSplit, { ViewDonorDetailsButton } from './AllocationSplit';
import TrueImpactDebug from '../../ReportBuilder/TrueImpactDebug';

interface DonorDetailsProps {
  task: Task;
  totalAmount: string | undefined;
  investmentFinalSplitIndex: number;
  className?: string;
  finishedIndexes: Set<number>;
  programNameByDonor: string | undefined;
  handleInvestmentIndexChange: (index: number) => unknown;
  investmentFinalSplit: Required<InvestmentSplitWithAnswers>[] | undefined;
}

const StyledDiv = styled.div`
  width: 217px;
  background-color: ${COLORS.grayLight};
  padding: ${SPACERS.small} ${SPACERS.medium};
  border-right: 1px solid ${COLORS.ctaPrimaryBackground};
  .heading-details {
    margin-bottom: 0;
  }
  .heading-details-sub {
    margin-bottom: ${SPACERS.small};
  }
  .heading-details-sub-email {
    word-break: break-all;
  }
  .donor-details-button {
    margin-bottom: ${SPACERS.small};
  }
`;

const DonorDetails: React.FC<DonorDetailsProps> = ({
  task,
  className,
  totalAmount,
  finishedIndexes,
  programNameByDonor,
  investmentFinalSplit,
  investmentFinalSplitIndex,
  handleInvestmentIndexChange,
}) => {
  const [isDebug, setIsDebug] = useState<boolean>(false);
  const [donorDetailsOpen, setDonorDetailsOpen] = useState<boolean>(false);

  const handleDebugClick = () => {
    console.log(
      totalAmount,
      finishedIndexes,
      programNameByDonor,
      investmentFinalSplit,
      investmentFinalSplitIndex,
      task,
    );
  };

  if (investmentFinalSplit && !donorDetailsOpen) {
    return (
      <AllocationSplit
        task={task}
        donorDetailsOpen={donorDetailsOpen}
        totalAmount={totalAmount}
        finishedIndexes={finishedIndexes}
        programNameByDonor={programNameByDonor}
        investmentFinalSplit={investmentFinalSplit}
        investmentFinalSplitIndex={investmentFinalSplitIndex}
        className={className}
        setDonorDetailsOpen={setDonorDetailsOpen}
        handleInvestmentIndexChange={handleInvestmentIndexChange}
      />
    );
  }
  // A task opened from the donations page carries the allocation, not the
  // invitation, so the investment has to be resolved from either shape.
  const investment = task.invitation?.investment || task.allocation?.investment;
  const relationshipManager = investment?.portfolio?.relationshipManager;
  const funderName = investment?.portfolio?.funder?.name;
  return (
    <StyledDiv className={className}>
      <TrueImpactDebug
        isDebug={isDebug}
        handleChange={setIsDebug}
        onClick={handleDebugClick}
      />
      {investmentFinalSplit && (
        <ViewDonorDetailsButton
          donorDetailsOpen={donorDetailsOpen}
          setDonorDetailsOpen={setDonorDetailsOpen}
          className="donor-details-button"
        />
      )}
      <TrueImpactText variant="heading-subheading">
        Donor-submitted details
      </TrueImpactText>
      <TrueImpactText
        color="blackBase"
        variant="heading-label"
        className="heading-details"
      >
        DONOR:
      </TrueImpactText>
      <TrueImpactText className="heading-details-sub">
        {funderName}
      </TrueImpactText>
      <TrueImpactText
        color="blackBase"
        variant="heading-label"
        className="heading-details"
      >
        AMOUNT:
      </TrueImpactText>
      <TrueImpactText className="heading-details-sub">
        {totalAmount}
      </TrueImpactText>
      <TrueImpactText
        color="blackBase"
        variant="heading-label"
        className="heading-details"
      >
        DONATION DATE:
      </TrueImpactText>
      <TrueImpactText className="heading-details-sub">
        {formattedDate(task.invitation?.createdAt || task.dueDate)}
      </TrueImpactText>
      <TrueImpactText
        color="blackBase"
        variant="heading-label"
        className="heading-details"
      >
        PORTFOLIO MANAGER:
      </TrueImpactText>
      <TrueImpactText className="heading-details-sub heading-details-sub-email">
        <FontAwesomeIcon icon={faEnvelopes} color={COLORS.ctaPrimary} />{' '}
        {relationshipManager ? (
          <>
            {`${relationshipManager.firstName} ${relationshipManager.lastName}`}
            {relationshipManager.email && (
              <>
                <br />({relationshipManager.email})
              </>
            )}
          </>
        ) : (
          funderName
        )}
      </TrueImpactText>
      <TrueImpactText
        color="blackBase"
        variant="heading-label"
        className="heading-details"
      >
        PROGRAM NAME BY DONOR:
      </TrueImpactText>
      <TrueImpactText className="heading-details-sub">
        {programNameByDonor}
      </TrueImpactText>
    </StyledDiv>
  );
};

export default DonorDetails;
